import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { StyledPaper, StyledGrid } from "@hcl-commerce-store-sdk/react-component";
import {
  StyledHeaderFooterLink,
  StyledTypography,
  StyledBox,
  StyledButton,
  StyledLink,
} from "../../../../../styled-mui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import AsyncCall from "../../../../../_foundation/gtm/async.service";
import { CLICK } from "../../../../../_foundation/constants/gtm";
import { HUBPROMO, MODELSLIST_BIS, PRISEDERDV, SERVICES_ATELIER } from "../../../../../constants/routes";
import { useSite } from "../../../../../_foundation/hooks/useSite";
import SmartLink from "../../../../elements/smart-link/smart-link";
import { palette } from "../../../../../themes/color-palette";
import { HeaderMegaMenuSX } from "../../../headerMegaMenu";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { GetTopCategorySelector } from "../../../../../redux/selectors/category";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router";
import { DesktopActionCard } from "../DesktopActionCards";
import { ImageLayout } from "../../../../elements/image";
import storeUtil from "../../../../../utils/storeUtil";
import { DiscountTagIcon } from "../../../NavgationBarSvg";

interface DesktopProductsMenuProps {
  closeHeaderMenu?: () => any;
  menuLinksData: Record<string, any>;
  pageParams?: Record<string, any> | null;
  categoriesProp?: Record<string, any>[];
  pathname?: string;
}

const DesktopProductsMenu: React.FC<DesktopProductsMenuProps> = ({
  closeHeaderMenu,
  menuLinksData,
  pageParams = null,
  categoriesProp = [],
  pathname = "",
}) => {
  const { mySite } = useSite();
  const { t } = useTranslation();
  const categories = useSelector(GetTopCategorySelector);
  const [activeMenuId, setActiveMenuId] = useState({ child0: 0, child1: 0 });
  const [catalogueLinks, setCatalogueLinks] = useState<{ label: string; url: string; target: string; type: string }[]>(
    []
  );
  const NAV_LEVEL_1 = "Produits";
  const activeCategory = categories[activeMenuId.child0];

  useEffect(() => {
    // Ensure required data is present
    if (!categories?.length || !menuLinksData?.otherMenuLinks?.length) {
      return;
    }
    const activeCategory = categories[activeMenuId.child0];
    // If no active category identifier, reset and return
    if (!activeCategory?.identifier) {
      setCatalogueLinks([]);
      return;
    }
    // Check if the identifier exists in any of the menuLinksData categories
    const isIdentifierPresent = menuLinksData.otherMenuLinks.some((item) =>
      item.categories?.includes(activeCategory.identifier)
    );
    if (isIdentifierPresent) {
      // Filter links to only show those that match the active category's identifier
      const filteredLinks = menuLinksData.otherMenuLinks
        .filter((item) => item.categories?.includes(activeCategory.identifier))
        .flatMap(
          (item) =>
            item.links?.map((link) => ({
              label: link.label,
              url: link.url,
              target: link.target,
              type: link.type,
            })) || []
        );

      setCatalogueLinks(filteredLinks);
    } else {
      setCatalogueLinks([]);
    }
  }, [categories, menuLinksData, activeMenuId]);

  const L2CategoriesData = useMemo(() => {
    const activeCategory = categories[activeMenuId.child0];
    if (!activeCategory?.children) return [];

    // Group children by their parent or show as individual columns
    return activeCategory.children.map((child: any) => ({
      title: child.name,
      items: child.children || [],
      seo: child.seo,
      identifier: child.identifier,
    }));
  }, [categories, activeMenuId.child0]);

  const pageType = pageParams?.page?.type || "";
  const currentIdentifier = pageParams?.identifier || pageParams?.tokenValue || null;
  const currentParent = pageParams?.parentCategoryId || null;

  const navigate = useNavigate();

  const isMagazineItem = (labelOrUrl: string, obj?: Record<string, any>) => {
    const label = (labelOrUrl || "").toString().toLowerCase();
    if (menuLinksData?.magazine && menuLinksData.magazine.label) {
      return label === menuLinksData.magazine.label.toString().toLowerCase();
    }
    return label.includes("magazine");
  };

  const shouldObfuscateItem = (itemIdentifier?: string, itemUrl?: string) => {
    // product pages: obfuscate all
    if (typeof pageType === "string" && pageType.toLowerCase().includes("product")) return true;

    // category pages
    if (typeof pageType === "string" && pageType.toLowerCase().includes("category")) {
      // magazine links must be obfuscated
      if (itemIdentifier && isMagazineItem(itemIdentifier)) return true;
      if (itemUrl && isMagazineItem(itemUrl)) return true;

      // top-level category page: keep subcategories of current visible (these are L2 for activeCategory)
      if (!currentParent) {
        // If this item is a child of activeCategory (we're rendering activeCategory children here) => do NOT obfuscate
        return false;
      }

      // lower-level category page: keep current category, its siblings (top-level) and its children
      if (itemIdentifier) {
        if (itemIdentifier === currentIdentifier) return false;
        // if item is a top-level category (sister)
        if (categories?.some((c) => c?.identifier === itemIdentifier)) return false;
        // if item is a direct child of currentIdentifier (we don't have full tree here) - attempt heuristic: compare parent ids if available
        // fallback: obfuscate
      }
      return true;
    }

    // default: do not obfuscate
    return false;
  };

  const onL1CatClick = (item: Record<string, any>, index: number) => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: item?.name || "",
      nav_level_3: "",
      nav_type: "cta",
      btn_label: item?.name || "",
      click_destination: item?.seo?.href || "",
    });
    setActiveMenuId({ child0: index, child1: 0 });
  };

  const onGoodDealsLinkClick = () => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: "",
      nav_level_3: "",
      nav_type: "promo_block",
      btn_label: "Voir les bonnes affaires",
      click_destination: HUBPROMO,
    });
    closeHeaderMenu && closeHeaderMenu();
    navigate(HUBPROMO);
  };

  const onL2AttachmentLinkClick = (item: Record<string, any>, activeCat: Record<string, any>) => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: activeCat?.name || "",
      nav_level_3: "",
      nav_type: "link",
      btn_label: item?.label || "",
      click_destination: item?.url || "",
    });
    closeHeaderMenu && closeHeaderMenu();
  };

  const onL2CatClick = (item: Record<string, any>, activeCat: Record<string, any>) => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: activeCat?.name || "",
      nav_level_3: item?.title || "",
      nav_type: "link",
      btn_label: item?.title || "",
      click_destination: item?.seo?.href || "",
    });
    closeHeaderMenu && closeHeaderMenu();
  };

  const onDiscoverUniversClick = (activeCat: Record<string, any>) => {
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: NAV_LEVEL_1,
      nav_level_2: activeCat?.name || "",
      nav_level_3: "",
      nav_type: "cta",
      btn_label: t("Header.Menu.Actions.DiscoverUniverse") + " " + activeCategory?.name,
      click_destination: activeCat?.seo?.href || "",
    });
    closeHeaderMenu && closeHeaderMenu();
  };

  return (
    <StyledPaper className="megamenu-wrapper expanded-menu-mega-menu-v2" sx={HeaderMegaMenuSX.wrapper}>
      <StyledGrid container sx={{ ...HeaderMegaMenuSX.container, minHeight: "440px" }}>
        {/* Left Navigation Column - Primary Categories */}
        <StyledGrid
          item
          className="expanded-menu-left-nav"
          md={4}
          sx={{
            ...HeaderMegaMenuSX.leftColumn,
            p: 0,
            paddingRight: { md: "24px !important" },
            m: 0,
            maxWidth: { lg: "365px" },
            flexBasis: { lg: "365px" },
            "@media (min-width:960px) and (max-width:1000px)": {
              width: "357px",
              maxWidth: "357px",
              flexBasis: "357px",
            },

            "@media (min-width: 1000px) and (max-width: 1100px)": {
              width: "365px",
              maxWidth: "365px",
              flexBasis: "365px",
            },

            "@media (min-width:1100px)": {
              width: "365px",
              maxWidth: "365px",
              flexBasis: "365px",
            },
          }}>
          <StyledTypography variant="body2" sx={{ ...HeaderMegaMenuSX.leftColumnHeading, fontSize: "13px" }}>
            UNIVERS
          </StyledTypography>

          {/* first column with top categories */}
          {categories && (
            <StyledBox component="ul" sx={HeaderMegaMenuSX.leftColumnItems}>
              {categories?.map((L1CatItem: any, index: number) => {
                const isActive = index === activeMenuId.child0;

                /* START OF link for top categories without children */
                let finalSeoHref = "";
                if (
                  (!mySite.storeCfg.userData.SEO_FE_DISABLE ||
                    (mySite.storeCfg.userData.SEO_FE_DISABLE && mySite.storeCfg.userData.SEO_FE_DISABLE !== "true")) &&
                  L1CatItem?.seo?.href?.split("/").length === 2
                ) {
                  const seoHref = L1CatItem?.seo.href;
                  finalSeoHref = `/produits${categories[activeMenuId.child0].seo?.href}${
                    categories[activeMenuId.child0]?.children[activeMenuId.child1].seo?.href
                  }${seoHref}`;
                } else {
                  finalSeoHref = L1CatItem?.seo?.href;
                }
                /* END OF link for top categories without children */

                return (
                  <StyledBox
                    component="li"
                    key={L1CatItem.id}
                    sx={[
                      HeaderMegaMenuSX.leftColumnMenuListItem,
                      isActive && {
                        backgroundColor: "#FFF6F5",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: "5px",
                          backgroundColor: palette.primary.main,
                          borderRadius: "0px 2px 2px 0px",
                        },
                      },
                    ]}>
                    {L1CatItem.children ? (
                      <StyledButton
                        fullWidth
                        variant="text"
                        onClick={() => onL1CatClick(L1CatItem, index)}
                        sx={[
                          HeaderMegaMenuSX.menuListItemButton,
                          { textTransform: "none" },
                          isActive && { color: "#C41E3B" },
                        ]}>
                        <ImageLayout
                          src={storeUtil.getUrlPicture(`img/contrib/categories/icons/${L1CatItem?.identifier}.svg`, "")}
                          height={
                            !storeUtil.getUrlPicture(`img/contrib/categories/icons/${L1CatItem?.identifier}.svg`, "")
                              ? 24
                              : undefined
                          }
                          width={
                            !storeUtil.getUrlPicture(`img/contrib/categories/icons/${L1CatItem?.identifier}.svg`, "")
                              ? 24
                              : undefined
                          }
                          styles={{ margin: "0px 12px 0px 0px" }}
                        />
                        {L1CatItem.name}
                      </StyledButton>
                    ) : (
                      <StyledBox sx={{ py: 0.5, px: 2 }}>
                        <span
                          className="js-link"
                          data-url={finalSeoHref}
                          role="link"
                          tabIndex={0}
                          data-testId={`header-${L1CatItem.id}`}
                          key={"Link_" + L1CatItem.id}
                          onClick={() => onL1CatClick(L1CatItem, 0)}
                          style={{ cursor: "pointer", display: "inline-block" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              onL1CatClick(L1CatItem, 0);
                            }
                          }}>
                          {L1CatItem.name}
                        </span>
                      </StyledBox>
                    )}
                  </StyledBox>
                );
              })}

              {/* First column, Bottom Link - Voir les bonnes affaires */}
              <StyledBox component="li">
                <StyledButton
                  variant="text"
                  startIcon={<DiscountTagIcon />}
                  endIcon={
                    <ArrowForwardIcon sx={{ color: palette.primary.main, ml: 0.5, width: "11px", height: "auto" }} />
                  }
                  className="link-hover-animateds"
                  data-url={HUBPROMO}
                  onClick={onGoodDealsLinkClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onGoodDealsLinkClick();
                    }
                  }}
                  sx={HeaderMegaMenuSX.staticLink}>
                  <span
                    className="js-link primary"
                    role="link"
                    tabIndex={0}
                    data-testId={`header-bonne-affaire`}
                    style={{ cursor: "pointer", display: "inline-block" }}>
                    <StyledTypography
                      variant="body4"
                      sx={{ fontSize: "14px", fontWeight: "600", lineHeight: "20px", ml: 1 }}>
                      {t("Header.Menu.Actions.SeeGoodDeals")}
                    </StyledTypography>
                  </span>
                </StyledButton>
              </StyledBox>
            </StyledBox>
          )}
        </StyledGrid>

        {/* Center Columns - L2 Sub-categories */}
        {activeCategory?.children && L2CategoriesData.length > 0 && (
          <StyledGrid
            item
            className="expanded-menu-center-columns"
            md={4}
            sx={{
              ...HeaderMegaMenuSX.leftColumn,
              p: 0,
              m: 0,
              width: { lg: "348px" },
              maxWidth: { lg: "348px" },
              flexBasis: { lg: "348px" },

              "@media (max-width: 1400px)": {
                width: "48%",
                maxWidth: "48%",
                flexBasis: "48%",
              },

              "@media (max-width: 1340px)": {
                width: "46%",
                maxWidth: "46%",
                flexBasis: "46%",
              },

              "@media (max-width: 1300px)": {
                width: "44%",
                maxWidth: "44%",
                flexBasis: "44%",
              },

              "@media (max-width: 1248px)": {
                width: "43%",
                maxWidth: "43%",
                flexBasis: "43%",
              },

              "@media (max-width: 1220px)": {
                width: "42%",
                maxWidth: "42%",
                flexBasis: "42%",
              },

              "@media (min-width:1401px)": {
                width: "348px",
                maxWidth: "348px",
                flexBasis: "348px",
              },
              px: { md: "20px" },
              py: { md: "30px" },
            }}>
            {/* Category Title and Description */}
            <StyledBox sx={{ mb: "28px", flexShrink: 0 }}>
              <StyledTypography variant="h4" sx={{ ...HeaderMegaMenuSX.rightColumnHeading, fontSize: "18px" }}>
                {activeCategory?.name}
              </StyledTypography>

              {/* PDF Catalog Link */}
              {catalogueLinks.length > 0 && (
                <StyledBox sx={{ mb: 1 }}>
                  {catalogueLinks.map((link: any, index: number) => {
                    const obf = shouldObfuscateItem(undefined, link.url);
                    return obf ? (
                      <span
                        key={index}
                        role="link"
                        tabIndex={0}
                        style={{ display: "inline-block", cursor: "pointer" }}
                        onClick={() => {
                          onL2AttachmentLinkClick(link, activeCategory);
                          if (link.target === "_blank") window.open(link.url, "_blank");
                          else if (link.url) navigate(link.url);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            onL2AttachmentLinkClick(link, activeCategory);
                            if (link.target === "_blank") window.open(link.url, "_blank");
                            else if (link.url) navigate(link.url);
                          }
                        }}>
                        <StyledBox component="span" sx={{ ...HeaderMegaMenuSX.extlinkText, fontSize: "14px" }}>
                          {link.label} <ArrowForwardIcon sx={{ ml: 0.5, width: "11px", height: "auto" }} />
                        </StyledBox>
                      </span>
                    ) : (
                      <SmartLink
                        key={index}
                        to={link.url}
                        isExternal={link.type === "external"}
                        onClick={() => onL2AttachmentLinkClick(link, activeCategory)}
                        sx={HeaderMegaMenuSX.extlink}>
                        <StyledBox component="span" sx={{ ...HeaderMegaMenuSX.extlinkText, fontSize: "14px" }}>
                          {link.label} <ArrowForwardIcon sx={{ ml: 0.5, width: "11px", height: "auto" }} />
                        </StyledBox>
                      </SmartLink>
                    );
                  })}
                </StyledBox>
              )}
            </StyledBox>

            {/* second column L2 CATEGORIES MAP */}
            <StyledBox sx={HeaderMegaMenuSX.catalogueItemsWrapper}>
              <StyledGrid container spacing={2}>
                {L2CategoriesData.map((L2CatItem: any, index: number) => {
                  const obfL2 = shouldObfuscateItem(L2CatItem?.identifier || L2CatItem?.title, L2CatItem?.seo?.href);
                  return (
                    <StyledGrid item xs={6} sm={12} md={12} lg={12} key={L2CatItem?.uniqueID}>
                      {obfL2 ? (
                        <span
                          role="link"
                          tabIndex={0}
                          style={{ display: "block", cursor: "pointer" }}
                          onClick={() => {
                            onL2CatClick(L2CatItem, activeCategory);
                            if (L2CatItem?.seo?.href)
                              navigate(L2CatItem?.identifier === "SCU1555698" ? MODELSLIST_BIS : L2CatItem?.seo?.href);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              onL2CatClick(L2CatItem, activeCategory);
                              if (L2CatItem?.seo?.href)
                                navigate(
                                  L2CatItem?.identifier === "SCU1555698" ? MODELSLIST_BIS : L2CatItem?.seo?.href
                                );
                            }
                          }}>
                          <StyledBox sx={{ marginBottom: "8px" }}>
                            <StyledTypography variant="p" sx={HeaderMegaMenuSX.catalogueItemHeading}>
                              {L2CatItem?.title}
                            </StyledTypography>
                          </StyledBox>
                        </span>
                      ) : (
                        <SmartLink
                          key={L2CatItem?.uniqueID}
                          to={L2CatItem?.identifier === "SCU1555698" ? MODELSLIST_BIS : L2CatItem?.seo?.href}
                          onClick={() => onL2CatClick(L2CatItem, activeCategory)}>
                          <StyledBox sx={{ marginBottom: "8px" }}>
                            <StyledTypography variant="p" sx={HeaderMegaMenuSX.catalogueItemHeading}>
                              {L2CatItem?.title}
                            </StyledTypography>
                          </StyledBox>
                        </SmartLink>
                      )}
                    </StyledGrid>
                  );
                })}
              </StyledGrid>

              {/* second column Bottom Button - Découvrir l'univers */}
              <StyledBox mt={3} pt={3}>
                <StyledButton
                  variant="outlined"
                  className="outlined-bold button-grey"
                  endIcon={<ArrowRightIcon sx={{ color: "#000" }} />}
                  onClick={() => onDiscoverUniversClick(activeCategory)}
                  sx={{
                    ...HeaderMegaMenuSX.navButtonPrimary,
                    justifyContent: "flex-start",
                    textAlign: "left",
                    alignItems: "flex-start",
                  }}>
                  <StyledHeaderFooterLink
                    to={activeCategory?.seo?.href || "#"}
                    testId={`header-discover-universe-${activeCategory?.id}`}
                    state={{
                      breadCrumbTrailEntryView: [
                        {
                          label: activeCategory?.name || "",
                          value: activeCategory?.id || "",
                          seo: activeCategory?.seo,
                        },
                      ],
                    }}
                    sx={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    {t("Header.Menu.Actions.DiscoverUniverse")} {activeCategory?.name || ""}
                  </StyledHeaderFooterLink>
                </StyledButton>
              </StyledBox>
            </StyledBox>
          </StyledGrid>
        )}

        {/* third column Right editorial blocks */}
        <Grid
          item
          xs={12}
          sm={6}
          md={"auto"}
          lg={3}
          sx={{
            display: "flex",
            padding: "10px",
            flexDirection: "column",
            gap: 4,
            flexBasis: { lg: "270px" },
            maxWidth: { lg: "270px" },

            "@media (max-width: 1400px)": {
              width: "270px",
              maxWidth: "270px",
              flexBasis: "270px",
            },

            "@media (min-width:1401px)": {
              width: "270px",
              maxWidth: "270px",
              flexBasis: "270px",
            },
            flexGrow: 0,
            m: 0,
            mt: { xs: 3 },
            p: 0, // padding: 0
            pl: "24px !important", // padding-left: 24px
            pt: "10px !important",
            // "@media (max-width: 1150px)": {
            //   display: "none",
            // },
          }}>
          <DesktopActionCard
            closeHeaderMenu={closeHeaderMenu}
            cardTheme="blue"
            chipName="SERVICES"
            title="DÉCOUPE SUR-MESURE"
            desc="Lapeyre recoupe vos produits en atelier ou en usine selon votre projet."
            btnLabel="EN SAVOIR PLUS"
            ctaLink={SERVICES_ATELIER}
            gtmData={{
              click_section: "menu",
              nav_level_1: NAV_LEVEL_1,
              nav_level_2: activeCategory?.name || "",
              nav_level_3: "",
              nav_type: "cta",
            }}
          />
          <DesktopActionCard
            closeHeaderMenu={closeHeaderMenu}
            cardTheme="red"
            chipName="AIDE"
            title="BESOIN DE CONSEIL"
            desc="Nos équipes sont là pour vous conseiller à chaque étape de votre projet."
            btnLabel="PRENDRE RENDEZ-VOUS"
            ctaLink={PRISEDERDV}
            rdvTracking={{
              navigation_location: "menu",
              click_destination: PRISEDERDV,
            }}
          />
        </Grid>
      </StyledGrid>
    </StyledPaper>
  );
};

export default DesktopProductsMenu;
